import { Alert } from '@repo/ui/components/ui/alert'
import { Button } from '@repo/ui/components/ui/button'
import { Callout } from '@repo/ui/components/ui/callout'
import { createFileRoute } from '@tanstack/react-router'
import { Bell, Check, Clock3 } from 'lucide-react'
import { DocumentRouteShell } from '@/components/app/route-shells'
import { AppTopBarActionGroup, AppTopBarActions } from '@/components/app/top-bar-actions'
import { NotificationFull } from '@/components/ui/notifications'
import { useNotifications } from '@/lib/notifications'
import type { RouteStaticData } from '@/lib/router/breadcrumbs'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_app/notifications')({
  component: NotificationsPage,
  staticData: {
    breadcrumbs: [{ label: 'Notifications', href: '/notifications' }],
  } satisfies RouteStaticData,
})

function NotificationsPage() {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications()

  return (
    <DocumentRouteShell>
      <AppTopBarActions>
        <AppTopBarActionGroup>
          {unreadCount > 0 ? (
            <Button onClick={markAllAsRead} variant="outline" className="cursor-pointer">
              <Check className="mr-2 h-4 w-4" />
              Mark all as read
            </Button>
          ) : null}
        </AppTopBarActionGroup>
      </AppTopBarActions>
      <div className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <Callout className="app-premium-panel border-border/70 px-5 py-4">
            <div className="text-sm font-medium">Unread</div>
            <div className="mt-2 text-2xl font-semibold">{unreadCount}</div>
          </Callout>
          <Callout className="app-premium-panel border-border/70 px-5 py-4">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Clock3 className="h-3.5 w-3.5" />
              Latest activity
            </div>
            <div className="mt-2 text-sm text-muted-foreground">
              {notifications[0]?.title || 'No recent notification'}
            </div>
          </Callout>
        </div>
        {notifications.length === 0 ? (
          <Callout className="app-premium-panel flex flex-col items-center justify-center px-6 py-12 text-center">
            <Bell className="mb-4 h-12 w-12 text-muted-foreground" />
            <h3 className="mb-2 text-lg font-medium">No notifications yet</h3>
            <p className="text-sm text-muted-foreground">
              Booking, billing, and workspace activity will appear here as the operator account is
              used.
            </p>
          </Callout>
        ) : (
          notifications.map((notification) => (
            <Alert
              key={notification.id}
              variant={notification.type === 'error' ? 'destructive' : 'default'}
              className={cn(
                'app-premium-panel transition-all',
                !notification.read && 'app-focus-ring'
              )}
            >
              <NotificationFull notification={notification} onMarkAsRead={markAsRead} />
            </Alert>
          ))
        )}
      </div>
    </DocumentRouteShell>
  )
}
